import { Fragment, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchForm } from '../store/form.slice'
import { fetchSubmission, setSubmission as setGlobalSubmission } from '../store/submission.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { formatNum, parseDate } from '../utils/functions'
import { QuestionSchema } from '../utils/types/form'

const SUPABASE_STORAGE_URL = process.env.REACT_APP_SUPABASE_STORAGE_URL

export const SubmissionView = () => {
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([])
  const [isLoading, setIsLoading] = useState({
    form: false,
    submission: false
  })
  const [requestState, setRequestState] = useState({ error: '' })
  const sumCheckboxQuestions = useRef(0)

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { form } = formState
  const submissionState = useAppSelector((state) => state.submission)
  const { data: globalSubmissions, submission } = submissionState
  const { formId, submissionId } = useParams()

  useEffect(() => {
    if (formId && submissionId) {
      populateData(formId, submissionId)
    }
    // eslint-disable-next-line
  }, [formId, submissionId])

  useEffect(() => {
    if (submission) {
      const allQuestions = submission.sections.flatMap(({ questions }) => questions)

      setAllQuestions(allQuestions)
    }
    // eslint-disable-next-line
  }, [submission])

  const populateData = async (formId: string, submissionId: string) => {
    const existingForm = form?._id === formId && form

    try {
      if (existingForm) {
        // Do nothing
      } else {
        setIsLoading({ ...isLoading, form: true })
        await dispatch(fetchForm(formId)).unwrap()
      }

      const existingSubmission =
        submission?._id === submissionId
          ? submission
          : globalSubmissions[formId]?.find(({ _id }) => _id === submissionId)

      if (existingSubmission) {
        dispatch(setGlobalSubmission(existingSubmission))
      } else {
        setIsLoading({ ...isLoading, submission: true })
        await dispatch(fetchSubmission({ formId, submissionId })).unwrap()
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ error: 'no_access' })
      } else {
        setRequestState({
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoading({ form: false, submission: false })
    }
  }

  const findQuestion = (questionId: string) => allQuestions?.find(({ id }) => id === questionId)

  return (
    <>
      {isLoading.form ? (
        <Spinner size='center' />
      ) : (
        <div className='main_content form_content'>
          <NavBack theme={form?.meta.colour} />
          {requestState.error ? (
            <>
              {requestState.error === 'no_access' ? (
                <Status warning='Form cannot be accessed' />
              ) : (
                <Status error={requestState.error} />
              )}
            </>
          ) : (
            <>
              {form && (
                <form className={`theme-content-${form.meta.colour}`}>
                  <header>
                    <h1 className='heading-styled'>{form.name}</h1>
                    <p>{form.description}</p>
                    {submission ? (
                      <div>
                        <span className='material-symbols-rounded'>pace</span>
                        <span className='subtext'>
                          {`${parseDate(
                            submission.submittedOn,
                            'medium_date'
                          )} (${parseDate(submission.submittedOn, 'time')})`}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className='subtext'>...</span>
                      </div>
                    )}
                  </header>
                  {isLoading.submission ? (
                    <span>...</span>
                  ) : (
                    <>
                      {submission &&
                        submission.sections.map((section) => {
                          sumCheckboxQuestions.current = 0

                          return (
                            <section key={section.id}>
                              {section.meta?.showHeader && (
                                <div className='header'>
                                  {section.name && <h2>{section.name}</h2>}
                                  <p>{section.description}</p>
                                </div>
                              )}

                              {section.questions.map((question, questionIndex) => {
                                if (question.conditional) {
                                  const { questionId, condition, value } = question.conditional

                                  const dependencyQuestion = findQuestion(questionId)

                                  if (dependencyQuestion) {
                                    if (
                                      condition === '>' &&
                                      !(Number(dependencyQuestion.answer) > Number(value))
                                    ) {
                                      return <Fragment key={questionIndex}></Fragment>
                                    } else if (
                                      condition === '<' &&
                                      !(Number(dependencyQuestion.answer) < Number(value))
                                    ) {
                                      return <Fragment key={questionIndex}></Fragment>
                                    }
                                  }
                                }

                                if (question.type === 'radio_checkbox') {
                                  sumCheckboxQuestions.current++
                                }

                                return (
                                  <div className='section_question' key={questionIndex}>
                                    {question.type === 'radio_checkbox' ? (
                                      <div />
                                    ) : (
                                      <span>
                                        {questionIndex + 1 - sumCheckboxQuestions.current}.
                                      </span>
                                    )}
                                    {question.type === 'radio_checkbox' ? (
                                      <div className='checkbox_container checkbox_container-radio checkbox_container-preview'>
                                        <div
                                          className={`radio-custom ${question.answer ? '' : 'radio-custom-preview'}`}>
                                          <input
                                            type='checkbox'
                                            checked={!!question.answer}
                                            readOnly
                                          />
                                          <span />
                                          <label>
                                            <span>{question.statement}</span>
                                            {question.description && (
                                              <em> ({question.description})</em>
                                            )}
                                            {question.required && (
                                              <span className='required'>*</span>
                                            )}
                                          </label>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <p>
                                          <span className='bold'>{question.statement}</span>
                                          {question.description && (
                                            <em> ({question.description})</em>
                                          )}
                                          {question.required && <span className='required'>*</span>}
                                        </p>
                                        <div />
                                      </>
                                    )}
                                    {((typeof question.answer === 'string' ||
                                      typeof question.answer === 'number') &&
                                      question.answer) ||
                                    (Array.isArray(question.answer) && question.answer.length) ? (
                                      <span className='highlight highlight-answer'>
                                        {question.type === 'file' ? (
                                          <>
                                            <em className='bold'>File:</em>{' '}
                                            <a
                                              href={`${SUPABASE_STORAGE_URL}/${question.answer}`}
                                              className='underline'
                                              target='_blank'
                                              rel='noreferrer'>
                                              {question.answer}
                                            </a>
                                          </>
                                        ) : (
                                          <>
                                            {question.type === 'checkbox' &&
                                            Array.isArray(question.answer)
                                              ? question.answer.join(', ')
                                              : question.type === 'radio_checkbox'
                                                ? question.answer
                                                  ? 'Yes'
                                                  : 'No'
                                                : question.type === 'date'
                                                  ? typeof question.answer === 'number' &&
                                                    parseDate(question.answer, 'short_date')
                                                  : question.type === 'rating'
                                                    ? `${question.answer} / ${question.meta?.ratingOptions?.length}`
                                                    : question.type === 'number'
                                                      ? `${formatNum(
                                                          question.answer as number
                                                        )}${question?.meta?.numberOptions?.unit ? ` ${question.meta.numberOptions.unit}` : ''}`
                                                      : question.answer}
                                          </>
                                        )}
                                      </span>
                                    ) : (
                                      <>{question.type !== 'radio_checkbox' && <span>-</span>}</>
                                    )}
                                  </div>
                                )
                              })}
                            </section>
                          )
                        })}
                    </>
                  )}
                </form>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}
